import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { embedLead } from "../api/leadApi";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Typography,
  Box,
  Link,
  CircularProgress,
} from "@mui/material";

const LeadCapture = () => {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const defaultSource = query.get("utm_source") || query.get("source") || "web";
  const defaultBuilderId = query.get("builder");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    budget: "",
    location_preference: "",
    timeline: "",
    visit_date: "",
    source: defaultSource,
    builder_id: defaultBuilderId || "",
  });

  const [status, setStatus] = useState({ loading: false, message: "", error: "" });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, message: "", error: "" });
    try {
      await embedLead(form);
      setStatus({ loading: false, message: "Lead submitted. We will contact you shortly.", error: "" });

      setForm({
        ...form,
        name: "",
        phone: "",
        budget: "",
        location_preference: "",
        timeline: "",
        visit_date: "",
      });
    } catch (err) {
      setStatus({
        loading: false,
        message: "",
        error: err.response?.data?.detail || "Failed to submit lead",
      });
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f5f5"
      py={4}
    >
      <Card sx={{ maxWidth: 500, width: "100%", padding: 2 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ mb: 3 }}>
            Book Your Visit
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="name"
              label="Name"
              value={form.name}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
            />

            <TextField
              fullWidth
              name="phone"
              label="Phone"
              value={form.phone}
              onChange={handleChange}
              required
              margin="normal"
              variant="outlined"
            />

            <TextField
              fullWidth
              name="budget"
              label="Budget"
              value={form.budget}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
            />

            <TextField
              fullWidth
              name="location_preference"
              label="Preferred Location"
              value={form.location_preference}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
            />

            <TextField
              fullWidth
              name="timeline"
              label="Timeline"
              value={form.timeline}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
            />

            {status.error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {status.error}
              </Alert>
            )}

            {status.message && (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                {status.message}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={status.loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {status.loading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} /> Submitting...
                </Box>
              ) : (
                "Submit Lead"
              )}
            </Button>
          </form>

          <Typography align="center" variant="body2" sx={{ mt: 2 }}>
            Prefer WhatsApp?{" "}
            <Link
              href={`https://wa.me/?text=Hi%2C%20I%20want%20to%20know%20more%20about%20properties.`}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{ cursor: "pointer", fontWeight: 600 }}
            >
              Chat with us
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LeadCapture;